import { useState } from 'react'
import { AddTicketsInput } from '../AddTickets/AddTickets';
import ToasterWithMessage from '../../components/shared-components/toaster';
import { CREATE_SUPER_USER, GET_ACTIVE_USERS } from '../AddTickets/api/addTicketsQuery';
import { useLazyQuery, useMutation } from '@apollo/client';
import styles from "../Settings/AppVersion.module.css";
import { Autocomplete, Button, InputAdornment, TextField } from "@mui/material";
import addTicketStyles from "../AddTickets/AddTickets.module.css";
import { getAutocompleteStyles } from '../../style/getAutoCompleteStyles';
import { set, useForm } from 'react-hook-form';
import { CustomTextField, InputPropsForAddAffiliates } from '../../helper';

const InputField = CustomTextField();

interface UsersList {
    u_id: string,
    u_email_id: string
}

interface SuperUserUpdateUserAndPercentageProps extends AddTicketsInput {
    refetchData: () =>Promise<any>,
    setSuperUsersList: (superUsersList: any) => void;
}
const SuperUserUpdateUserAndPercentage = ({
    ticketDetails,
    setTicketDetails,
    refetchData,
    setSuperUsersList
}: SuperUserUpdateUserAndPercentageProps) => {

    const [percentage, setPercentage] = useState('');
    const [listOfUsers, setListOfUsers] = useState([]);
    const [isNextBtnClicked, setIsNextBtnClicked] = useState(false);
    const [searchUsers, setSearchUsers] = useState<UsersList | null>(null);
    const [checkPercentage,setCheckPercentage] = useState<number>(0);
    const [zeroValueErrorForPercentage,setZeroValueErrorForPercentage] = useState<string>('');
    const { error,success } = ToasterWithMessage();
     const {register,handleSubmit,trigger,setValue,watch,formState: {errors},setError} = useForm(
            {
                defaultValues: {
                    referralBonus: '',
                    affiliateBonus: '',
                    cutAmount: '',
                    percentage: ''
                },
                mode: "onChange"});

    const [getUsers, { loading: usersLoading }] = useLazyQuery(GET_ACTIVE_USERS, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setListOfUsers(data?.user);
        },
        onError: (err) => {
            console.log(err, "ERROR WHILE GETTING LIST OF USERS");
            error(err?.message);
        }
    });

    const [promoteUserToSuperUser, { loading: promoteUserToSuperUserLoading }] = useMutation(CREATE_SUPER_USER, {
            fetchPolicy: "no-cache",
            onCompleted: (data) => {
                success(data?.promoteUserToSuperUser?.message);
            },
            onError: (err) => {
                console.log(err.message, "===error")
                error(err?.message);
            }
    }); 

    const watchedPercentage = watch("percentage"); // react-hook-form watch

    const isAddButtonDisabled = !searchUsers || 
                            !watchedPercentage || 
                            parseFloat(watchedPercentage) < 8 || 
                            parseFloat(watchedPercentage) > 20;

    const handleChangeCutAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputData = e.target.value;
        setZeroValueErrorForPercentage("");

        const regexPattern = /^(0|[1-9]\d*)(\.\d{0,2})?$/;

        if (inputData === "") {
            setPercentage("");
            setValue("percentage", "");
            trigger("percentage"); 
            return;
        }

        if (!regexPattern.test(inputData)) {
            return;
        }
        const numericValue = parseFloat(inputData);

        if (numericValue > 20 || numericValue < 8) {
            setError("percentage", {
            type: "manual",
            message: "Percentage must be between 8 and 20",
            });
            setPercentage(inputData); 
            setValue("percentage", inputData);
            return;
        }
        setPercentage(inputData);
        setValue("percentage", inputData);
        trigger("percentage");
        };


    const handleSearchUsers = (event: any, value: any) => {
        setSearchUsers(value);
        setTicketDetails({
            ...ticketDetails,
            userId: value?.u_id,
            userEmail: value?.u_email_id
        });
    }

    const handleInput = async (_event: any, value: any) => {
        if (value.length >= 3) {
            await getUsers({
                variables: {
                    searchText: `${value}%`
                }
            })
        }
    }


    const handleAddSuperUser = async() => {
        setIsNextBtnClicked(true);
        if(!searchUsers || !percentage || parseFloat(percentage) === 0){
            return;
        }
        await promoteUserToSuperUser({
            variables: {
                emailId: searchUsers?.u_email_id,
                percentage: parseFloat(percentage)
            }
        });

        console.log("Before fetching super users in handleAddusers");
        const res = await refetchData();
        setSuperUsersList(res?.data);
        console.log("Refetched data after promoteUser:",res);
        setIsNextBtnClicked(false);
        setSearchUsers(null);
        setPercentage('');
        setTicketDetails(null);
    }

    return (
        <div className={styles.textFieldFullMainappversion + ' ' + styles.eventsUpdateFieldsWrap}>
            <div className={styles.submainFieldsWrapper}>
                <div className={addTicketStyles.container + ' ' + addTicketStyles.eventsUpdateFieldExtra}>
                    <div className={styles.titlePlaceholderappversion} style={{ marginBottom: '10px' }}>
                        <b className={styles.AppOs}>Users</b>
                    </div>
                    <div>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            sx={getAutocompleteStyles(true)}
                            options={listOfUsers ?? []}
                            loading={usersLoading}
                            value={searchUsers}
                            onChange={handleSearchUsers}
                            onInputChange={handleInput}
                            autoHighlight
                            getOptionLabel={(option: UsersList) => option.u_email_id || "-"}
                            isOptionEqualToValue={(option: UsersList, value: UsersList) => option.u_id === value.u_id}
                            getOptionKey={(option: UsersList) => option.u_id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Users"
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        />
                    </div>
                    {
                        isNextBtnClicked && !searchUsers && <span className={addTicketStyles.errorMessage}>User is required!</span>
                    }
                </div>

                <div className={addTicketStyles.container + ' ' + addTicketStyles.eventsUpdateFieldExtra}>
                    <div className={styles.titlePlaceholderappversion} style={{ marginBottom: '10px' }}>
                        <b className={styles.AppOs}>Percentage</b>
                    </div>
                    <div>
                        <InputField
                            variant="outlined"
                            data-testid='percentage'
                            {...register("percentage", {
                                required: "Percentage is required!",
                                    min: { value: 8, message: "Percentage must be at least 8" },   
                                    max: { value: 20, message: "Percentage must be at most 20"},
                                    validate: (val:any) => {
                                        const numeric = parseFloat(val);
                                        if (isNaN(numeric)) return "Invalid number";
                                        if (numeric <= 0) return "Value must be greater than zero";
                                        return true;
                                    }, 
                                
                            })}
                            value={percentage}
                            placeholder={'Enter percentage'}
                            InputProps={{
                                style: InputPropsForAddAffiliates,
                                endAdornment: (
                                    <InputAdornment position="start">
                                        {`%`}
                                    </InputAdornment>
                                )
                            }}
                            error={Boolean(errors?.percentage?.message) || Boolean(zeroValueErrorForPercentage)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeCutAmount(e)}
                        />
                    </div>
                    {
                       errors.percentage && <span className={addTicketStyles.errorMessage}>{errors.percentage.message}</span>
                    }
                </div>

                <div style={{ width: '100%', display: 'flex', marginTop: "25px" }}>
                    <div className={addTicketStyles.updateButtonStyle} style={{width:'max-content',cursor: isAddButtonDisabled ? 'not-allowed' : 'pointer'}}>
                        <Button
                            classes={{
                                root: styles.separateStyleReferral
                            }}
                            disableRipple
                            fullWidth
                            type="button"
                            disabled={isAddButtonDisabled}
                            onClick={handleAddSuperUser}
                        >
                            Add Super User
                        </Button>
                    </div>
                </div>

            </div>


        </div>
    )
}

export default SuperUserUpdateUserAndPercentage;